import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Typography,
  Divider,
  Breadcrumbs,
  Link as MuiLink,
  TextField,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import DataGridInfoAlert from "../../components/dataAdminGrid/InfoAlert";
import DataAdminGrid from "../../components/dataAdminGrid/DataAdminGrid";
import { ROUTES } from "../../constants";
import {
  CONTACTS_BASE_URL,
  COLUMNS as BASE_COLUMNS,
  CONTACT_PK_FIELD,
} from "./contactsConfig";

import AutocompleteEditCell from "../../components/dataAdminGrid/AutocompleteEditCell";
import { useContactLookups } from "./useContactLookups";

function ContactsPage() {
  const { contactRoles } = useContactLookups();
  const [searchText, setSearchText] = useState("");

  const rolesById = useMemo(() => {
    const map = new Map();
    for (const role of contactRoles) {
      map.set(role.role_ndx, role);
    }
    return map;
  }, [contactRoles]);

  const normalizedNeedle = searchText.trim().toLowerCase();

  const rowMatchesSearch = useMemo(() => {
    if (!normalizedNeedle) return () => true;

    return (row) => {
      if (row?.contact_ndx == null) return true;

      const roleDesc = rolesById.get(row?.role_ndx)?.role_desc;

      const searchable = [
        row?.contact_ndx,
        row?.name_company,
        row?.name_last,
        row?.name_first,
        row?.role_ndx,
        roleDesc,
        row?.address,
        row?.city,
        row?.state,
        row?.zip_code,
        row?.phone_primary,
        row?.phone_alternate,
        row?.phone_fax,
        row?.email,
        row?.num_tceq,
        row?.num_license,
      ]
        .filter((v) => v !== undefined && v !== null)
        .join(" ")
        .toLowerCase();

      return searchable.includes(normalizedNeedle);
    };
  }, [normalizedNeedle, rolesById]);

  const columns = useMemo(() => {
    return BASE_COLUMNS.map((col) => {
      if (col.field === "role_ndx") {
        return {
          ...col,
          renderCell: ({ value }) => {
            const found = contactRoles.find((z) => z.role_ndx === value);
            return found ? (
              found.role_desc
            ) : (
              <Typography
                sx={{
                  fontStyle: "italic",
                  color: "text.secondary",
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                No Role Selected
              </Typography>
            );
          },
          renderEditCell: (params) => (
            <AutocompleteEditCell
              {...params}
              options={contactRoles}
              optionIdField="role_ndx"
              optionLabelField="role_desc"
              multiple={false}
              label="Role"
            />
          ),
        };
      }
      return col;
    });
  }, [contactRoles]);

  return (
    <Box>
      <Helmet title="Contacts Management" />

      <Typography variant="h3" gutterBottom display="inline">
        Contacts Management
      </Typography>

      <Breadcrumbs sx={{ mt: 4 }} aria-label="breadcrumb">
        <MuiLink component={RouterLink} to={ROUTES.LANDING}>
          Home
        </MuiLink>
        <Typography>Contacts</Typography>
      </Breadcrumbs>

      <Divider sx={{ my: 6 }} />

      <DataGridInfoAlert title="Manage your contacts:" />

      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          label="Search"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </Box>

      <DataAdminGrid
        baseUrl={CONTACTS_BASE_URL}
        columns={columns}
        primaryKeyField={CONTACT_PK_FIELD}
        rowFilter={rowMatchesSearch}
      />
    </Box>
  );
}

export default ContactsPage;
